import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Event, EventStats } from '../types/Event';
import { COLLECTION_NAMES } from '../utils/constants';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

let cachedEvents: Event[] | null = null;
let cacheTimestamp = 0;

/**
 * Fetch all upcoming events (event_date >= today) from Firestore,
 * ordered by event_date ascending.
 */
export async function getUpcomingEvents(): Promise<Event[]> {
  const now = Date.now();
  if (cachedEvents && now - cacheTimestamp < CACHE_DURATION) {
    return cachedEvents;
  }

  const todayISO = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const q = query(
    collection(db, COLLECTION_NAMES.EVENTS),
    where('event_date', '>=', todayISO),
    orderBy('event_date', 'asc'),
  );

  const snapshot = await getDocs(q);
  const events: Event[] = snapshot.docs.map((doc) => ({
    ...(doc.data() as Omit<Event, 'id'>),
    id: doc.id,
  }));

  cachedEvents = events;
  cacheTimestamp = Date.now();

  return events;
}

/**
 * Invalidate the local cache so the next call fetches fresh data.
 */
export function clearEventsCache(): void {
  cachedEvents = null;
  cacheTimestamp = 0;
}

/**
 * Calculate aggregate stats from a list of events.
 */
export function calculateEventStats(events: Event[]): EventStats {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const oneWeekFromNow = new Date(today);
  oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);

  const oneMonthFromNow = new Date(today);
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

  let thisWeek = 0;
  let thisMonth = 0;
  const citySet = new Set<string>();

  for (const event of events) {
    const eventDate = new Date(event.event_date + 'T00:00:00');
    if (eventDate >= today && eventDate < oneWeekFromNow) {
      thisWeek++;
    }
    if (eventDate >= today && eventDate < oneMonthFromNow) {
      thisMonth++;
    }
    if (event.location?.city) {
      citySet.add(event.location.city);
    }
  }

  return {
    totalEvents: events.length,
    thisWeek,
    thisMonth,
    cities: Array.from(citySet).sort(),
  };
}

/**
 * Filter events by the selected cities (case-insensitive).
 *
 * The selected cities are combined with OR: an event matches if its city is
 * included in the selected array. An empty or absent array means the filter is
 * inactive and matches every event.
 */
export function filterEventsByCity(events: Event[], cities: string[]): Event[] {
  if (!cities || cities.length === 0) return events;
  const lowerSet = new Set(cities.map((c) => c.toLowerCase()));
  return events.filter((e) => {
    const city = e.location?.city?.toLowerCase();
    return city !== undefined && lowerSet.has(city);
  });
}

/**
 * Search events by name (case-insensitive partial match).
 */
export function searchEvents(events: Event[], searchQuery: string): Event[] {
  if (!searchQuery.trim()) return events;
  const lower = searchQuery.toLowerCase();
  return events.filter((e) => e.name.toLowerCase().includes(lower));
}
